import React from 'react';
import { Trash2, AlertTriangle, Target, Briefcase, Clock, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';

interface Objective {
  id: string;
  employeeName: string;
  employeeAvatar?: string;
  department: string;
  focus: string;
  linkedRole: string;
  horizon: string;
  createdDate: string;
}

interface DeleteObjectiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDelete: (id: string) => void;
  objective: Objective | null;
}

const DeleteObjectiveModal: React.FC<DeleteObjectiveModalProps> = ({ isOpen, onClose, onDelete, objective }) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const handleDelete = () => {
    onDelete?.(objective?.id!);
    onClose?.();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-3">
              <Trash2 className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{t('Delete Development Objective')}</h2>
              <p className="text-sm text-gray-600">{t('This action cannot be undone')}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <p className="text-gray-900 mb-4">{t('Are you sure you want to delete this development objective?')}</p>
            {objective && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center space-x-3 pb-3 border-b border-border">
                  {objective?.employeeAvatar ? (
                    <img
                      src={objective?.employeeAvatar}
                      alt={`${objective?.employeeName} profile photo`}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white text-sm font-medium">
                      {objective?.employeeName?.split(' ')?.map(n => n?.[0])?.join('')}
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900">{objective?.employeeName}</p>
                    <p className="text-xs text-gray-600">{objective?.department}</p>
                  </div>
                </div>
                <div className="flex items-start text-sm">
                  <Target className="w-3.5 h-3.5 text-gray-600 mr-2 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-gray-900">{t('Focus')}:</span>
                    <p className="text-gray-600 mt-1">{objective?.focus}</p>
                  </div>
                </div>
                <div className="flex items-center text-sm">
                  <Briefcase className="w-3.5 h-3.5 text-gray-600 mr-2" />
                  <span className="font-medium text-gray-900">{t('Role')}:</span>
                  <span className="ml-2 text-gray-600">{objective?.linkedRole}</span>
                </div>
                <div className="flex items-center text-sm">
                  <Clock className="w-3.5 h-3.5 text-gray-600 mr-2" />
                  <span className="font-medium text-gray-900">{t('Horizon')}:</span>
                  <span className="ml-2 text-gray-600">{objective?.horizon}</span>
                </div>
                <div className="flex items-center text-sm">
                  <Calendar className="w-3.5 h-3.5 text-gray-600 mr-2" />
                  <span className="font-medium text-gray-900">{t('Created')}:</span>
                  <span className="ml-2 text-gray-600">
                    {format(parseISO(objective?.createdDate), 'MMM d, yyyy')}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
            <AlertTriangle className="w-4 h-4 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-800">
              {t('Deleting this objective will permanently remove all associated data including reflections, evidence, and coaching references.')}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 bg-gray-50 border-t border-border flex items-center justify-end space-x-3 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-border rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
          >
            {t('Cancel')}
          </button>
          <button
            onClick={handleDelete}
            className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 transition-colors flex items-center"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {t('Delete Objective')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteObjectiveModal;
